import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { graphql } from 'gatsby';
import sizes from 'react-sizes';
import styled from 'styled-components';
import { format } from 'date-fns';
import { Card, ContactUs, Hero, HeroImg, SEO, SimilarCards } from 'components';
import { LinkedinShareButton, FacebookShareButton, TwitterShareButton } from 'react-share';
import LinkedInSVG from 'images/shared/contact_form/linkedin_icon.svg';
import FBSVG from 'images/shared/contact_form/facebook_icon.svg';
import TwitterSVG from 'images/shared/contact_form/twitter_icon.svg';

const BlogPost = sizes(({ width }) => ({ viewportWidth: width && width }))(
  ({ data, location, handleHeader, handleEventManagementTrendsBannerLoading }) => {
    const { href, pathname } = location;
    const { blogPost, similarBlogPosts, recentBlogPosts } = data;
    const { html, frontmatter } = blogPost;
    const { title, themes, date, summary, featuredImg } = frontmatter;

    useEffect(() => {
      const eventManagementBannerEC = document.querySelector('.event-management-banner-ec');
      if (eventManagementBannerEC) {
        handleEventManagementTrendsBannerLoading();
      }
    }, []);

    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
      target: containerRef,
      offset: ['start end', 'end start']
    });

    const y = useTransform(scrollYProgress, [0, 0.1], [60, 0]);

    return (
      title && (
        <>
          <Hero
            title={title}
            subText={summary}
            date={format(new Date(date), 'MMM do, yyyy')}
            themes={themes}
            handleHeader={handleHeader}>
            <HeroImg heroImg={featuredImg} />
          </Hero>
          <motion.div ref={containerRef} style={{ y }}>
            <BodyAndRecentBlogPosts>
              <div>
                <SocialMediaShareButtons>
                  <span style={{ marginRight: '4px' }}>Share On</span>
                  <LinkedinShareButton url={href}>
                    <SocialMediaShareButton>
                      <img src={LinkedInSVG} alt="LinkedIn logo" width="14px" />
                    </SocialMediaShareButton>
                  </LinkedinShareButton>
                  <FacebookShareButton url={href}>
                    <SocialMediaShareButton>
                      <img src={FBSVG} alt="Facebook logo" width="8px" />
                    </SocialMediaShareButton>
                  </FacebookShareButton>
                  <TwitterShareButton url={href}>
                    <SocialMediaShareButton>
                      <img src={TwitterSVG} alt="Twitter logo" width="17px" />
                    </SocialMediaShareButton>
                  </TwitterShareButton>
                </SocialMediaShareButtons>
                <Body dangerouslySetInnerHTML={{ __html: html }} />
              </div>
              <RecentBlogPosts>
                <span>Recent Posts</span>
                <div>
                  {recentBlogPosts.edges.map(({ node: recentBlogPost }) => (
                    <Card
                      date={recentBlogPost.frontmatter.date}
                      title={recentBlogPost.frontmatter.title
                        .split(' ')
                        .map((word) => word.slice(0, 1).toUpperCase() + word.slice(1))
                        .join(' ')}
                      summary={recentBlogPost.frontmatter.summary}
                      featuredImg={recentBlogPost.frontmatter.featuredImg}
                      pathname={pathname}
                    />
                  ))}
                </div>
              </RecentBlogPosts>
            </BodyAndRecentBlogPosts>
            <SimilarCards
              similarCards={similarBlogPosts.edges}
              buttonTitle="All Blog Posts"
              title="Similar Blog Posts"
              linkPath="blog"
              pathname={pathname}
              paddingStyle="0 4.8%"
            />
          </motion.div>
          <ContactUs location={location} />
        </>
      )
    );
  }
);

export const Head = ({ data, location }) => {
  const { frontmatter } = data.blogPost;
  return (
    <SEO
      pageSpecificTitle={
        frontmatter.metaTagOverrides?.title ? frontmatter.metaTagOverrides.title : frontmatter.title
      }
      pageSpecificDescription={
        frontmatter.metaTagOverrides?.description
          ? frontmatter.metaTagOverrides.description
          : frontmatter.summary
      }
      pageSpecificThumbnail={
        frontmatter.featuredImg.src.childImageSharp.gatsbyImageData.images.fallback.src
      }
      pageSpecificThumbnailAlt={frontmatter.featuredImg.src.name.replace(/-|_/g, ' ')}
      pathname={location.pathname}
    />
  );
};

const BodyAndRecentBlogPosts = styled.div`
  align-items: center;
  display: flex;
  font-family: 'proxima-nova', sans-serif;
  flex-direction: column;
  margin: 0 auto;
  max-width: 1159px;
  padding: 10% 12px 87px;

  @media screen and (min-width: 1200px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: 8% 12px 4rem;
  }
`;

const Body = styled.section`
  max-width: 690px;

  p {
    font-size: 1rem;
    line-height: 1.313em;
    margin-bottom: 2.125em;
    color: var(--dynamic-dark-grey);
  }

  a {
    color: var(--dynamic-dark-blue);
  }

  h1 {
    font-size: 1.75rem;
  }

  h2 {
    font-size: 1.5rem;
  }

  h3 {
    font-size: 1.25rem;
  }

  h4 {
    font-size: 1.125rem;
  }

  h5 {
    font-size: 1rem;
  }

  h1,
  h2,
  h3,
  h4,
  h5 {
    font-family: adelle, serif;
    margin: -0.075em 0 0.25em;
  }

  figure,
  > span.gatsby-resp-image-wrapper,
  div.video-without-caption {
    margin: 2rem 0;
  }

  figcaption {
    font-size: 0.85rem;
    margin-top: 0.25em;
    color: #3b444b;
    text-align: center;
  }

  iframe {
    /* max-width: 690px; */
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }

  ul,
  ol {
    font-size: 1rem;
    margin-bottom: 2.125em;
  }

  ul {
    margin-left: 1.25rem;
  }

  blockquote {
    text-align: center;
    font-style: italic;
    margin-bottom: 2.125em;

    p {
      font-size: 1.5rem;
      margin: 0;
    }
  }

  a {
    color: var(--dynamic-blue);
  }
`;

const RecentBlogPosts = styled.section`
  margin-top: 1rem;
  max-width: 350px;
  width: 100%;

  > span {
    font-size: 1.25rem;
  }

  > div {
    margin: 35px 0 0;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 60px 0;
  }

  @media screen and (min-width: 1200px) {
    margin-top: 0;
  }
`;

const SocialMediaShareButtons = styled.div`
  align-items: center;
  color: #4f4f4f !important;
  display: flex;
  font-size: 12px;
  font-weight: 800;
  justify-content: space-between;
  margin-bottom: 40px;
  text-transform: uppercase;
  width: 190px;
`;

const SocialMediaShareButton = styled.div`
  align-items: center;
  background-color: var(--dynamic-blue);
  border-radius: 50%;
  display: flex;
  height: 2rem;
  justify-content: center;
  width: 2rem;

  &:hover {
    background-color: var(--dynamic-purple);
    transition: background-color 100ms ease-in-out;
  }
`;

export const query = graphql`
  query ($title: String!, $themes: [String!]!) {
    blogPost: markdownRemark(frontmatter: { title: { eq: $title } }) {
      id
      html
      frontmatter {
        title
        summary
        metaTagOverrides {
          title
          description
        }
        themes
        date
        featuredImg {
          alt
          src {
            name
            childImageSharp {
              gatsbyImageData(
                width: 1440
                transformOptions: { cropFocus: CENTER }
                quality: 50
                pngOptions: { compressionSpeed: 10 }
                placeholder: BLURRED
                formats: [AUTO, WEBP]
              )
            }
          }
        }
      }
    }
    similarBlogPosts: allFile(
      limit: 3
      filter: {
        childMarkdownRemark: { frontmatter: { title: { ne: $title }, themes: { in: $themes } } }
        sourceInstanceName: { eq: "cms_data_blog_posts" }
        ext: { eq: ".md" }
      }
    ) {
      edges {
        node {
          id
          childMarkdownRemark {
            frontmatter {
              title
              summary
              themes
              date
              featuredImg {
                alt
                src {
                  childImageSharp {
                    gatsbyImageData(
                      width: 350
                      height: 200
                      transformOptions: { cropFocus: CENTER }
                      formats: [AUTO, WEBP]
                    )
                  }
                }
              }
            }
          }
        }
      }
    }
    recentBlogPosts: allMarkdownRemark(
      filter: {
        fileAbsolutePath: { regex: "/blog_posts/" }
        frontmatter: { title: { ne: $title } }
      }
      sort: { frontmatter: { date: DESC } }
      limit: 3
    ) {
      edges {
        node {
          id
          excerpt(pruneLength: 178)
          frontmatter {
            date
            title
            summary
            featuredImg {
              alt
              src {
                childImageSharp {
                  gatsbyImageData(
                    width: 350
                    height: 214
                    transformOptions: { cropFocus: CENTER }
                    quality: 40
                    pngOptions: { compressionSpeed: 10 }
                    placeholder: BLURRED
                    formats: [AUTO, WEBP]
                  )
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default BlogPost;
